// src/features/task/create/AddAction.tsx
// Secondary content lives behind an add-action, not in a permanently open
// section. "+ Checklist" is an offer; the checklist editor appears under the
// row only once it has been accepted.
//
// An add-action is rendered only when the selected task type actually enables
// what it opens, so the row never offers something that cannot be used. Once a
// section holds content the button carries a count — collapsed is never the
// same thing as lost.

import type { ReactNode } from 'react';
import { cn } from '@core/ui';
import { Icon, CREATE_ICONS } from './icons';

export interface AddActionProps {
  icon: string;
  label: string;
  /** How much this section already holds. Hidden when zero. */
  count?: number;
  open: boolean;
  onToggle: () => void;
  /** DOM id of the section this button expands. */
  controls: string;
  disabled?: boolean;
}

export function AddAction({
  icon,
  label,
  count,
  open,
  onToggle,
  controls,
  disabled,
}: AddActionProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      aria-expanded={open}
      aria-controls={open ? controls : undefined}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        'disabled:cursor-not-allowed disabled:opacity-50',
        open ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
      )}
    >
      <Icon d={open ? icon : CREATE_ICONS.plus} className="h-3.5 w-3.5 shrink-0" />
      {label}
      {count ? (
        <span className="rounded-full bg-primary/15 px-1.5 text-xs font-medium tabular-nums text-primary">
          {count}
        </span>
      ) : null}
    </button>
  );
}

export interface CreateSectionProps {
  id: string;
  title: string;
  /** One short line explaining what happens on create. */
  hint?: string;
  /** Controls aligned right of the heading (manage links). */
  aside?: ReactNode;
  children: ReactNode;
}

/** An expanded add-action: a quiet heading and the editor it opened. */
export function CreateSection({ id, title, hint, aside, children }: CreateSectionProps) {
  return (
    <section id={id} className="rounded-lg border border-border bg-muted/20 p-3">
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <div className="flex min-w-0 items-baseline gap-2">
          <h2 className="shrink-0 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {title}
          </h2>
          {hint && <p className="truncate text-xs text-muted-foreground/80">{hint}</p>}
        </div>
        {aside}
      </div>
      {children}
    </section>
  );
}
